import React from "react";

import ExternalLinkMD from "@busbud/design-system-components/dist/Icons/ExternalLink/MD";
import LongArrowRightMD from "@busbud/design-system-components/dist/Icons/LongArrowRight/MD";
import Skeleton from "@busbud/design-system-components/dist/Skeleton";
import Stack from "@busbud/design-system-components/dist/Stack";
import type { ButtonProps } from "@busbud/horizon";
import { Button } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { TripOption, TripOptionsMatrix } from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import { PassengerAndClassInformation } from "./passenger-and-class-information";
import { PriceInformation } from "./price-information";

interface Props {
  is_external?: boolean;
  selected_trip?: TripOption;
  step_label: string;
  search: SearchInfo;
  disabled?: boolean;
  onClickContinue: (selected_trip: TripOption) => void;
  is_roundtrip?: boolean;
  segment_id?: string;
  trip_options_matrix: TripOptionsMatrix;
}

const PriceAndPassengersInformation: React.FC<
  Pick<Props, "search" | "disabled" | "segment_id" | "trip_options_matrix"> & {
    selected_trip: TripOption;
  }
> = ({ disabled, search, selected_trip, segment_id, trip_options_matrix }) => {
  return (
    <Stack direction="column" spacing={0.5} alignItems="flex-end">
      <PriceInformation
        disabled={disabled}
        search={search}
        selected_trip={selected_trip}
        segment_id={segment_id}
      />
      <PassengerAndClassInformation
        search={search}
        selected_trip={selected_trip}
        trip_options_matrix={trip_options_matrix}
      />
    </Stack>
  );
};

export const Summary: React.FC<Omit<Props, "step_label">> = ({
  is_external,
  selected_trip,
  disabled = false,
  onClickContinue,
  search,
  segment_id,
  is_roundtrip,
  trip_options_matrix
}) => {
  const { translator } = useAppContext();
  const is_loading = !selected_trip?.price_in_cents;

  const button_props: ButtonProps & { children: string } =
    is_external && !is_roundtrip
      ? {
          children: translator.t("!results.select-referral-departure"),
          iconEnd: <ExternalLinkMD />
        }
      : {
          children: translator.t("!results.classes-fares.continue"),
          iconEnd: <LongArrowRightMD />
        };

  return (
    <Stack
      spacing={3}
      alignItems="center"
      justifyContent="flex-end"
      mr={0.5}
      ml="auto"
    >
      {is_loading ? (
        <Stack direction="column" justifyContent="space-between" spacing={1}>
          <Skeleton width={15} size="md" />
          <Skeleton width={15} size="sm" />
        </Stack>
      ) : (
        <PriceAndPassengersInformation
          selected_trip={selected_trip}
          search={search}
          disabled={disabled}
          segment_id={segment_id}
          trip_options_matrix={trip_options_matrix}
        />
      )}
      <Button
        data-cy="confirm-departure-selection"
        isDisabled={is_loading || disabled}
        appearance="primary"
        onClick={() => {
          if (!selected_trip) {
            return;
          }

          onClickContinue(selected_trip);
        }}
        size="lg"
        {...button_props}
      />
    </Stack>
  );
};
